<script setup>
import Footer from "@/components/Footer.vue";
import SearchBar from "@/components/SearchBar.vue";
import Card from "@/components/ui/card/Card.vue";
import CardContent from "@/components/ui/card/CardContent.vue";
import CardHeader from "@/components/ui/card/CardHeader.vue";
import HomeView from "@/views/HomeView.vue";
import { useEventsStore } from "@/stores/events.js";
import { useUserDataStore } from "@/stores/userData.js";
import { computed, ref, watch } from "vue";
import {
  isEventActive,
  isEventUpcoming,
  formatEventDate,
} from "@/lib/dateUtils.js";
import EventView from "@/views/EventView.vue";
import DesktopEventView from "@/views/DesktopEventView.vue";
import { formatDate, useBreakpoints, breakpointsTailwind } from "@vueuse/core";
import { stringToTrigrams, levenshtein } from "@/lib/stringUtils.js";
import Navigation from "@/components/Navigation.vue";

const breakpoints = useBreakpoints(breakpointsTailwind);
const isMobile = breakpoints.smaller("lg"); // true if screen width is less than 'lg' breakpoint

// Presenter logic
const eventsStore = useEventsStore();
const userDataStore = useUserDataStore();

const eventsTrigrams = computed(() =>
  eventsStore.events.map((event) => ({
    id: event.id,
    trigrams: event.name ? stringToTrigrams(event.name) : new Set(),
    descTrigrams: event.description
      ? stringToTrigrams(event.description)
      : new Set(),
  }))
);

const isOpen = ref(false);
const selectedEvent = ref(null); // New ref for selected event
const seat = ref(null);
const signedUp = ref(false);

function openEventDetails(event) {
  selectedEvent.value = event;
  isOpen.value = true;

  // Find if user is signed up for the event
  signedUp.value = userDataStore.events.includes(event.id);

  // Find seat for the selected event
  const seat_location = eventsStore.getSeatBookingForUser(
    event,
    userDataStore.steamId
  );

  if (seat_location) {
    const adjustment = seat_location[1] > 2 ? -1 : 0; // Adjust for missing column
    const colLetter = String.fromCharCode(65 + seat_location[1] + adjustment);
    const rowNumber = seat_location[0];
    seat.value = colLetter + rowNumber;
  } else {
    seat.value = "No seat booked";
  }
}

function closeEventDetails() {
  isOpen.value = false;
  selectedEvent.value = null;
}

const searchQuery = ref("");

function searchTermChanged(newTerm) {
  searchQuery.value = newTerm;
}

const searchTrigrams = computed(() => stringToTrigrams(searchQuery.value));

const filteredEvents = computed(() => {
  if (
    !searchQuery.value ||
    searchQuery.value.trim() === "" ||
    searchTrigrams.value.size === 0
  ) {
    return eventsStore.events;
  }

  return eventsStore.events.filter((event) => {
    const eventTrigramObj = eventsTrigrams.value.find((e) => e.id === event.id);
    if (!eventTrigramObj) return false;

    const nameMatch =
      searchTrigrams.value.intersection(eventTrigramObj.trigrams).size > 0;
    const descMatch =
      searchTrigrams.value.intersection(eventTrigramObj.descTrigrams).size > 0;

    return nameMatch || descMatch;
  });
});

function sortBySearchLevenshtein(eventList) {
  return eventList
    .map((e) => [
      e,
      levenshtein(e.name.toLowerCase(), searchQuery.value.toLowerCase()),
    ])
    .sort((a, b) => a[1] - b[1])
    .map((e) => e[0]);
}

const activeEvents = computed(() =>
  sortBySearchLevenshtein(
    filteredEvents.value.filter((event) => isEventActive(event))
  )
);
const upcomingEvents = computed(() =>
  sortBySearchLevenshtein(
    filteredEvents.value.filter((event) => isEventUpcoming(event))
  )
);
</script>

<template>
  <!-- Mobile Layout -->
  <template v-if="isMobile">
    <Card class="rounded-none border-none shadow-none bg-background">
      <CardHeader>
        <SearchBar
          :modelValue="searchQuery"
          @change="searchTermChanged"
        ></SearchBar>
      </CardHeader>
      <CardContent>
        <HomeView
          :openEventACB="openEventDetails"
          :activeEvents="activeEvents"
          :upcomingEvents="upcomingEvents"
          :isReady="eventsStore.eventsReady"
          :signedUpEvents="userDataStore.events"
        />
      </CardContent>
      <Footer></Footer>
    </Card>

    <!-- Overlay for dismissing the event view -->
    <div
      v-if="isOpen"
      class="fixed inset-0 bg-[rgba(0,0,0,0.25)] z-90"
      @click="closeEventDetails"
    ></div>

    <EventView
      :class="[
        'card-overlay',
        { 'card-overlay--open': isOpen, 'card-overlay--closed': !isOpen },
        'eventInfoCard',
        'fixed',
        'bottom-0',
        'left-0',
        'w-full',
        'border-none',
        'rounded-t-md',
        'z-100',
        'scrollable',
      ]"
      :name="selectedEvent ? selectedEvent.name : ''"
      :date="formatEventDate(selectedEvent)"
      :description="selectedEvent?.description"
      :location="selectedEvent?.location"
      :signedUp="signedUp"
      :img="selectedEvent?.imageUrl"
      :seat="seat"
      :id="selectedEvent?.id"
      :userAuthenticated="userDataStore?.isLoggedIn"
      @dismiss="closeEventDetails"
    ></EventView>
  </template>

  <!-- Desktop Layout -->
  <template v-else>
    <div class="w-full h-20">
      <Navigation
        :username="userDataStore?.userProfile?.nickname || 'Guest'"
        :avatarUrl="userDataStore?.avatarUrl || ''"
        :isLoggedIn="userDataStore?.isLoggedIn"
      ></Navigation>
    </div>

    <Card class="bg-background border-0 rounded-lg shadow-none">
      <CardHeader>
        <SearchBar
          :modelValue="searchQuery"
          @change="searchTermChanged"
        ></SearchBar>
      </CardHeader>
      <CardContent>
        <HomeView
          :openEventACB="openEventDetails"
          :activeEvents="activeEvents"
          :upcomingEvents="upcomingEvents"
          :isReady="eventsStore.eventsReady"
          :signedUpEvents="userDataStore.events"
        />
      </CardContent>
    </Card>

    <div
      v-if="isOpen"
      class="fixed inset-0 bg-[rgba(0,0,0,0.75)] z-90 flex items-center justify-center"
      @click.self="closeEventDetails"
    >
      <DesktopEventView
        class=""
        :name="selectedEvent ? selectedEvent.name : ''"
        :date="formatEventDate(selectedEvent)"
        :description="selectedEvent?.description"
        :location="selectedEvent?.location"
        :signedUp="signedUp"
        :img="selectedEvent?.imageUrl"
        :seat="seat"
        :id="selectedEvent?.id"
        :userAuthenticated="userDataStore?.isLoggedIn"
        @dismiss="closeEventDetails"
      ></DesktopEventView>
    </div>
  </template>
</template>

<style scoped>
.card-overlay {
  transition: bottom 0.3s ease-in-out;
  overflow: hidden; /* Ensure content is hidden when height is 0 */
}

.card-overlay--closed {
  bottom: -88dvh;
  height: 88dvh;
}

.card-overlay--open {
  bottom: 0;
  height: 88dvh;
}
</style>
